import 'components/ordering/quotation/components/modal/modal.js'
import { customElement, query } from 'lit/decorators.js'
import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { consume } from '@lit/context'
import {
  activeQuotationContext,
  updateQuotationContext
} from 'components/ordering/quotation/components/context/quotation-context.js'
import { hostDisplayContents } from 'components/ordering/quotation/components/quotation.style.js'
import { QuotationDTO } from 'components/ordering/quotation/api/dtos/quotation-dto.js'
import { NotFoundError } from 'components/ordering/quotation/api/dtos/error.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { isQuotationDTO } from 'components/ordering/quotation/components/is-quotation-dto.js'
import { CSCQuotationModal } from 'components/ordering/quotation/components/modal/modal.js'
import { UpdateQuotationPayload } from 'components/ordering/quotation/api/requests/update-quotation.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'

initializeIconsByRole({
  'csc-quotation-answer-buttons.decline-button': { name: 'xmark', style: 'solid' },
  'csc-quotation-answer-buttons.accept-button': { name: 'check', style: 'solid' },
})

@customElement('csc-quotation-answer-buttons')
export class CSCQuotationAnswerButtons extends LitElement {
  static override styles: CSSResultGroup = [hostDisplayContents]

  @consume({ context: activeQuotationContext, subscribe: true })
  private quotation!: QuotationDTO | NotFoundError

  @query('csc-quotation-modal[target-id="decline"]')
  private _declineModal!: CSCQuotationModal | null | undefined

  private renderDeclineButton() {
    return html`
      <csc-quotation-modal target-id="decline" type="decline"></csc-quotation-modal>

      <cse-button
        variant="error"
        fit="auto"
        @click=${() => this._declineModal?.open()}
      >
        <cse-icon icon-role="csc-quotation-answer-buttons.decline-button" slot="prefix"></cse-icon>
        ${t('decline_button', {
          ns: 'csc-quotation-answer-buttons',
          default: 'Decline'
        })}
      </cse-button>
    `
  }

  @consume({ context: updateQuotationContext })
  private updateQuotation!: (payload: UpdateQuotationPayload) => Promise<void>

  private async accept(): Promise<void> {
    if (!isQuotationDTO(this.quotation)) return

    await this.updateQuotation({
      quotationId: this.quotation.id,
      status: 'accepted',
    })
  }

  private renderAcceptButton() {
    if (!isQuotationDTO(this.quotation)) {
      return nothing
    }

    return html`
      <cse-button
        variant="success"
        fit="auto"
        ?disabled="${!this.quotation.canBeAccepted}"
        @click="${this.accept}"
      >
        <cse-icon icon-role="csc-quotation-answer-buttons.accept-button" slot="prefix"></cse-icon>
        ${t('accept_button', {
          ns: 'csc-quotation-answer-buttons',
          default: 'Accept'
        })}
      </cse-button>
    `
  }

  override render() {
    if (!isQuotationDTO(this.quotation) || this.quotation.status !== 'answered' || this.quotation.priceRequestMode) {
      return nothing
    }

    return html`
      <cse-button-row>
        ${this.renderDeclineButton()}
        ${this.renderAcceptButton()}
      </cse-button-row>
    `
  }
}
